/* eslint-disable react-refresh/only-export-components -- AuthProvider + useAuth hook live together by convention */
import React from 'react';
import type { Session, User as SupabaseAuthUser } from '@supabase/supabase-js';
import { selfServiceSupabase } from '../../../lib/selfServiceSupabase';
import { apiClient, ApiError } from '../../../lib/apiClient';
import { isSelfServiceDevelopment } from '../../../lib/env';
import * as analytics from '../../../lib/analytics';
import { EVENTS } from '../../../lib/analytics';

// Real Supabase Auth replaces the previous localStorage-based mock.
//
//   - requestOtp → POST /auth/sign-in/request (selfservice-api mints the
//                  Supabase token via admin.generateLink + dispatches our
//                  SendGrid `auth.signin` template — see ADR 0001)
//   - verifyOtp  → supabase.auth.verifyOtp({ email, token, type: 'email' })
//   - identity   → supabase.auth.getSession() / onAuthStateChange (real JWT)
//
// The session JWT is consumed by the selfservice-api NestJS service via the
// centralized apiClient. Profile-side fields (business_name, contact_name…)
// are still kept in a local React state slot until the corresponding
// /profiles API endpoints are wired up in Phase 4 of the migration.

export type SelfServiceRole = 'advertiser' | 'publisher' | 'admin';

// The raw `app_metadata.role` from the Supabase JWT. Kept alongside the
// coarse `role` because the UI sometimes needs to distinguish org_admin from
// manager/sales_rep (both surface as 'publisher' in the simplified role).
export type SelfServiceRawRole =
  | 'super_admin'
  | 'organization_admin'
  | 'manager'
  | 'sales_rep'
  | 'advertiser';

// Per-Supabase-user identity exposed to the rest of the app. Business identity
// (business name + website) is no longer a user field — it lives on the
// advertiser seat (see AdvertiserMembership.advertiserName / businessWebsite).
// Personal contact info comes from /profiles/me.
export interface SelfServiceUser {
  id: string;
  email: string;
  contactName?: string;
  contactPhone?: string;
  role?: SelfServiceRole;
  rawRole?: SelfServiceRawRole;
  organizationId?: string;
  publisherSlug?: string; // populated when the JWT scopes the user to a station
  /**
   * Whether the caller's `auth.users` row has an `encrypted_password`. Hydrated
   * from `GET /auth/me/has-password` on session change. `null` while the
   * initial fetch is in flight — components deciding whether to nudge the
   * user to set a password should treat null as "don't show it yet."
   */
  hasPassword: boolean | null;
}

// CTV-415 seat redesign: the advertiser is a shared seat. Multi-membership is
// allowed (any user can be on any number of advertiser seats), so the wizard
// has to track which seat is currently selected. Selection persists in
// localStorage (`SELECTED_ADVERTISER_KEY`) so a returning user lands on the
// same seat they last used.
export interface AdvertiserMembership {
  membershipId: string;
  advertiserId: string;
  advertiserName: string;
  businessWebsite: string | null;
  role: string;
  acceptedAt: string | null;
}

// CTV-415 — stations the signed-in staff user can administer (driven by
// JWT scoping + the publisher_admin_grants check). Fetched once on session
// change; powers the /admin picker and the sidebar station switcher.
export interface AdminStation {
  slug: string | null;
  name: string;
  displayName: string | null;
  organizationId: string;
}

interface AuthContextValue {
  user: SelfServiceUser | null;
  isAuthenticated: boolean;
  /**
   * CTV-415 Phase 2. Accepted advertiser memberships for the signed-in user.
   * `null` while the initial /memberships/mine fetch is in flight; `[]` when
   * the user has no memberships (e.g. publisher staff, super_admin, or a
   * fresh advertiser who hasn't claimed their first submission yet).
   */
  advertiserMemberships: AdvertiserMembership[] | null;
  /** Force a refetch of /memberships/mine, e.g. right after accepting an invite. */
  refreshMemberships: () => Promise<void>;
  /**
   * CTV-582 "eager membership seed": inject a membership directly into the
   * `advertiserMemberships` list (upsert by advertiserId) without waiting for
   * a follow-up /memberships/mine round-trip. Used by callers of
   * POST /memberships/ensure-self so the wizard body can rebuild
   * `selectedAdvertiser` in the same tick the seat is created, closing the
   * post-verify hydration handoff race described in
   * `CONTEXT.md → Post-verify hydration (CTV-582)`.
   */
  seedMembership: (m: AdvertiserMembership) => void;
  /**
   * The advertiser the user is currently "acting as." Derived from
   * advertiserMemberships + selectedAdvertiserId. `null` when the user has no
   * memberships (anon, or first-time buyer who hasn't completed step 4 yet).
   */
  selectedAdvertiser: AdvertiserMembership | null;
  /** Switch the active seat. Persists to localStorage. */
  setSelectedAdvertiserId: (advertiserId: string) => void;
  /**
   * CTV-415 Phase 2b. Whether the signed-in user has an active publisher-admin
   * grant. `null` while the RPC is in flight; `true` / `false` thereafter.
   * Errors deny by default (i.e. surface as `false`) so a transient supabase
   * outage can't accidentally hand out wizard admin access.
   */
  hasPublisherAdminGrant: boolean | null;
  /** Refetch /profiles/me, e.g. after BusinessInfoStep commits new fields. */
  refreshProfile: () => Promise<void>;
  /**
   * CTV-415 — stations the signed-in staff user can administer. `null` while
   * the initial fetch is in flight; `[]` when the user has no admin access
   * (advertisers, ungranted staff, signed-out users).
   */
  myAdminStations: AdminStation[] | null;
  refreshMyAdminStations: () => Promise<void>;
  /**
   * Force a refetch of GET /auth/me/has-password. Called by SignInModal
   * immediately after a successful OTP verify (to decide whether to show the
   * "Set a password" nudge) and after the user finishes setting one, so the
   * whole app sees the fresh value without waiting for the background effect.
   * Returns the fresh value (or `null` on error / signed-out) so callers can
   * branch on it without waiting for a re-render.
   */
  refreshHasPassword: () => Promise<boolean | null>;
  signOut: () => Promise<void>;
  /** Persist a profile patch to selfservice_advertiser_profiles. Requires sign-in. */
  updateProfile: (patch: Partial<SelfServiceUser>) => Promise<void>;
  /** Sends the OTP via Supabase Auth. devCode is no longer available — kept for shape compatibility. */
  requestOtp: (email: string, stationSlug?: string) => Promise<{ devCode: string }>;
  verifyOtp: (email: string, code: string) => Promise<SelfServiceUser>;
  /**
   * True when the signed-in user is allowed to administer the given station.
   * - super_admin: any station
   * - organization_admin: stations whose organizationId matches user.organizationId
   * - manager / sales_rep: stations whose `name` is in user_metadata.stations
   * - everyone else: false
   */
  canAdminStation: (config: {
    name: string;
    organizationId: string | null;
  }) => boolean;
}

const AuthContext = React.createContext<AuthContextValue | null>(null);

// Keys we wipe on sign-out — drafts and code-attribution localStorage entries
// belong to the previous tab/user and should not bleed into the next session.
const DRAFT_PREFIX = 'ctvbuyer.selfservice.draft.';
const CODE_PREFIX_REF = 'selfservice:ref-code:';
const CODE_PREFIX_PROMO = 'selfservice:promo-code:';
// Currently-selected advertiser seat (per-browser). Persists across reloads
// so users land on the same seat they last used. Cleared on sign-out.
const SELECTED_ADVERTISER_KEY = 'ctvbuyer.selfservice.selectedAdvertiserId';

function clearWizardCaches() {
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (
        k &&
        (k.startsWith(DRAFT_PREFIX) ||
         k.startsWith(CODE_PREFIX_REF) ||
         k.startsWith(CODE_PREFIX_PROMO))
      ) {
        toRemove.push(k);
      }
    }
    for (const k of toRemove) localStorage.removeItem(k);
    localStorage.removeItem(SELECTED_ADVERTISER_KEY);
  } catch { /* private mode — non-fatal */ }
}

function readSelectedAdvertiserId(): string | null {
  try {
    return localStorage.getItem(SELECTED_ADVERTISER_KEY);
  } catch {
    return null;
  }
}

function writeSelectedAdvertiserId(id: string | null) {
  try {
    if (id) localStorage.setItem(SELECTED_ADVERTISER_KEY, id);
    else localStorage.removeItem(SELECTED_ADVERTISER_KEY);
  } catch { /* private mode — non-fatal */ }
}

// Map Supabase JWT app_metadata.role → the simpler SelfServiceRole shown
// to UI components. Manager / sales_rep / organization_admin all surface as
// 'publisher' (they administer one or more stations); super_admin is 'admin';
// 'advertiser' is the default for the consumer portal.
function mapRole(authUser: SupabaseAuthUser | null | undefined): SelfServiceRole | undefined {
  const raw = authUser?.app_metadata?.role;
  if (raw === 'super_admin') return 'admin';
  if (raw === 'organization_admin' || raw === 'manager' || raw === 'sales_rep') return 'publisher';
  if (raw === 'advertiser') return 'advertiser';
  return undefined;
}

const RAW_ROLES: readonly SelfServiceRawRole[] = [
  'super_admin',
  'organization_admin',
  'manager',
  'sales_rep',
  'advertiser',
];

// The subset of raw roles that map to CTV Buyer "staff" — used by gates on
// /onboard, /settings/security, and the staff sign-in flow. Exported so the
// three consumer sites don't each maintain their own Set literal.
export const STAFF_RAW_ROLES: ReadonlySet<SelfServiceRawRole> = new Set([
  'super_admin',
  'organization_admin',
  'manager',
  'sales_rep',
]);

function extractRawRole(authUser: SupabaseAuthUser | null | undefined): SelfServiceRawRole | undefined {
  const raw = authUser?.app_metadata?.role;
  return typeof raw === 'string' && (RAW_ROLES as readonly string[]).includes(raw)
    ? (raw as SelfServiceRawRole)
    : undefined;
}

// Existing repo convention puts organization_id under user_metadata; the
// seeded local admin uses app_metadata. Read either so both work.
function extractOrganizationId(authUser: SupabaseAuthUser | null | undefined): string | undefined {
  const fromUser = (authUser?.user_metadata as Record<string, unknown> | undefined)?.organization_id;
  const fromApp = authUser?.app_metadata?.organization_id;
  const orgId = fromUser ?? fromApp;
  return typeof orgId === 'string' && orgId.length > 0 ? orgId : undefined;
}

// JWT carries station NAMES (or a single legacy `user_metadata.station`).
// We surface the first one as a "current" publisherSlug for the UI to match
// against the URL slug. Strict slug↔name resolution is the API's job in
// Phase 4 — until then this is a best-effort match by exact name.
function extractStations(authUser: SupabaseAuthUser | null | undefined): string[] {
  const meta = (authUser?.user_metadata ?? {}) as Record<string, unknown>;
  if (Array.isArray(meta.stations)) {
    return (meta.stations as unknown[]).filter((x): x is string => typeof x === 'string' && x.length > 0);
  }
  if (typeof meta.station === 'string' && meta.station.length > 0) return [meta.station];
  return [];
}

// DB-backed shape returned from GET /profiles/me. Mirrors the
// selfservice_advertiser_profiles row in lowercase camelCase. Null when
// the user has not yet saved a profile. Seat redesign: businessName +
// businessWebsite moved to the advertiser; profile is just personal contact
// info now.
interface AdvertiserProfile {
  userId: string;
  contactEmail: string;
  contactName: string | null;
  contactPhone: string | null;
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = React.useState<Session | null>(null);
  // Persisted profile for the signed-in user (DB-backed via /profiles/me).
  // Replaced the previous in-memory `profilesByEmail` map so business info
  // survives sign-out / new-browser / refresh. Null while loading or when
  // the user has not yet completed BusinessInfoStep.
  const [profile, setProfile] = React.useState<AdvertiserProfile | null>(null);
  const [bootstrapped, setBootstrapped] = React.useState(false);
  const [advertiserMemberships, setAdvertiserMemberships] = React.useState<
    AdvertiserMembership[] | null
  >(null);
  // CTV-415 seat redesign: the currently-active advertiser seat. Hydrated
  // from localStorage on first mount; validated against the memberships list
  // (an obsolete id gets cleared and replaced with the first available one).
  const [selectedAdvertiserId, setSelectedAdvertiserIdState] = React.useState<string | null>(
    () => readSelectedAdvertiserId(),
  );
  // Phase 2b RPC: gates publisher-staff access to wizard admin views. `null`
  // while the RPC is in flight, then true/false. Errors deny by default — see
  // the effect below.
  const [hasPublisherAdminGrant, setHasPublisherAdminGrant] = React.useState<boolean | null>(
    null,
  );
  // Stations the user can administer (intersection of JWT scope + grant).
  // `null` while loading; `[]` when no access. Drives /admin picker UX.
  const [myAdminStations, setMyAdminStations] = React.useState<AdminStation[] | null>(null);
  // Whether the signed-in user has an `encrypted_password` on their auth row.
  // `null` while loading or signed-out; true/false once /auth/me/has-password
  // resolves. Drives the post-OTP "Set a password" nudge in SignInModal.
  const [hasPassword, setHasPassword] = React.useState<boolean | null>(null);

  // Bootstrap: read the existing session, then subscribe to changes.
  React.useEffect(() => {
    let cancelled = false;
    if (!selfServiceSupabase) {
      setBootstrapped(true);
      return;
    }
    selfServiceSupabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      setSession(data.session ?? null);
      setBootstrapped(true);
    }).catch(() => {
      if (cancelled) return;
      setBootstrapped(true);
    });
    const { data: sub } = selfServiceSupabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
    });
    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  // CTV-468 analytics: identify by Supabase user id (no PII) once a session
  // exists; reset on sign-out. auth_state rides on every event as context.
  // analytics.identify is internally idempotent, so the per-route AuthProvider
  // re-mount doesn't re-fire it on each navigation.
  React.useEffect(() => {
    if (!bootstrapped) return;
    const userId = session?.user?.id;
    if (userId) {
      analytics.identify(userId);
      // role + publisher_slug ride on every event so advertiser / publisher /
      // admin audiences are separable in PostHog (and the advertiser funnels
      // can be filtered to role=advertiser to drop publisher/staff noise).
      analytics.registerContext({
        auth_state: 'authenticated',
        // Any authenticated non-staff user is an advertiser on this portal;
        // mapRole only returns publisher/admin/explicit-advertiser, so default
        // the rest to 'advertiser' to keep the super-property always set.
        role: mapRole(session?.user) ?? 'advertiser',
        publisher_slug: extractStations(session?.user)[0] ?? null,
      });
    } else {
      analytics.reset();
      analytics.registerContext({ auth_state: 'anonymous' });
    }
  }, [session?.user, bootstrapped]);

  const buildUser = React.useCallback(
    (authUser: SupabaseAuthUser | null | undefined): SelfServiceUser | null => {
      if (!authUser?.email) return null;
      const stations = extractStations(authUser);
      // Profile fields come from the DB-backed `profile` state. They're empty
      // strings / undefined until /profiles/me resolves; BusinessInfoStep
      // refills them once it does.
      const profileForUser = profile?.userId === authUser.id ? profile : null;
      return {
        id: authUser.id,
        email: authUser.email,
        contactName: profileForUser?.contactName ?? undefined,
        contactPhone: profileForUser?.contactPhone ?? undefined,
        role: mapRole(authUser),
        rawRole: extractRawRole(authUser),
        organizationId: extractOrganizationId(authUser),
        publisherSlug: stations[0],
        hasPassword,
      };
    },
    [profile, hasPassword],
  );

  const user = React.useMemo(() => buildUser(session?.user), [session, buildUser]);

  // Fetch /memberships/mine whenever the authenticated user changes. The
  // result drives advertiser-side routing: a user with at least one accepted
  // membership lands on /my-campaigns; without one, they only see the wizard
  // submission flow (publisher staff also use this — they'll typically have
  // zero memberships but still pass through publisher admin paths).
  //
  // CTV-582: reads the current user id from the live Supabase session at
  // call time instead of the React `user` snapshot. The wizard's `onVerified`
  // handler captures this callback on the pre-verify render, when the React
  // `user` is still null — a captured-`user?.id` gate would trip and clobber
  // `advertiserMemberships` back to null right after the seat was created.
  // The live session is populated the moment `verifyOtp` returns, so the
  // getSession() read here always sees the freshly-signed-in user id.
  // Deps are `[]` so the reference is stable — no captured state to go stale.
  const refreshMemberships = React.useCallback(async () => {
    if (!selfServiceSupabase) {
      setAdvertiserMemberships(null);
      return;
    }
    const { data } = await selfServiceSupabase.auth.getSession();
    const currentUserId = data.session?.user?.id;
    if (!currentUserId) {
      setAdvertiserMemberships(null);
      return;
    }
    try {
      const resp = await apiClient.get<{ memberships: AdvertiserMembership[] }>(
        '/memberships/mine',
      );
      setAdvertiserMemberships(resp.memberships ?? []);
    } catch (e) {
      // Don't blow up the UI for a memberships fetch failure — leaves
      // advertiserMemberships at its previous value. Components that hard-
      // depend on memberships should render a loading / retry state when
      // it's null and the user is authenticated.
      console.warn('[useAuth] memberships fetch failed:', e);
    }
  }, []);

  // CTV-582 "eager membership seed": upsert a membership into the list by
  // advertiserId. Used right after POST /memberships/ensure-self returns so
  // the wizard body sees `selectedAdvertiser` populated in the same commit
  // that flips `advertiserId` non-null — no /memberships/mine round-trip,
  // no race with a stale-closure refresh.
  const seedMembership = React.useCallback((m: AdvertiserMembership) => {
    setAdvertiserMemberships((prev) => {
      const list = prev ? [...prev] : [];
      const i = list.findIndex((x) => x.advertiserId === m.advertiserId);
      if (i >= 0) list[i] = m;
      else list.push(m);
      return list;
    });
  }, []);

  React.useEffect(() => {
    if (!user?.id) {
      setAdvertiserMemberships(null);
      return;
    }
    void refreshMemberships();
  }, [user?.id, refreshMemberships]);

  // Keep selectedAdvertiserId in sync with the memberships list:
  //   - if memberships load and the stored id isn't in the list anymore
  //     (revoked, switched account), fall back to the first membership
  //   - if memberships is empty, clear the selection
  //   - if memberships is null (still loading), do nothing
  React.useEffect(() => {
    if (advertiserMemberships === null) return;
    if (advertiserMemberships.length === 0) {
      if (selectedAdvertiserId !== null) {
        setSelectedAdvertiserIdState(null);
        writeSelectedAdvertiserId(null);
      }
      return;
    }
    const stillValid = selectedAdvertiserId
      ? advertiserMemberships.some((m) => m.advertiserId === selectedAdvertiserId)
      : false;
    if (!stillValid) {
      const next = advertiserMemberships[0].advertiserId;
      setSelectedAdvertiserIdState(next);
      writeSelectedAdvertiserId(next);
    }
  }, [advertiserMemberships, selectedAdvertiserId]);

  const setSelectedAdvertiserId = React.useCallback((advertiserId: string) => {
    setSelectedAdvertiserIdState(advertiserId);
    writeSelectedAdvertiserId(advertiserId);
  }, []);

  const selectedAdvertiser = React.useMemo<AdvertiserMembership | null>(() => {
    if (!advertiserMemberships || !selectedAdvertiserId) return null;
    return advertiserMemberships.find((m) => m.advertiserId === selectedAdvertiserId) ?? null;
  }, [advertiserMemberships, selectedAdvertiserId]);

  // Fetch the DB-backed profile every time the authenticated user changes.
  // Replaces the previous in-memory `profilesByEmail` shim so business info
  // persists across sign-outs and devices.
  const refreshProfile = React.useCallback(async () => {
    if (!session?.user?.id) {
      setProfile(null);
      return;
    }
    try {
      const data = await apiClient.get<AdvertiserProfile | null>('/profiles/me');
      setProfile(data ?? null);
    } catch (e) {
      // 404 is the not-yet-saved case — service returns null instead, but
      // surface other errors so we know if the API is down.
      console.warn('[useAuth] profile fetch failed:', e);
    }
  }, [session?.user?.id]);

  React.useEffect(() => {
    if (!session?.user?.id) {
      setProfile(null);
      return;
    }
    void refreshProfile();
  }, [session?.user?.id, refreshProfile]);

  // Fetch the staff user's admin-station list (super_admin → all; staff →
  // intersection of JWT scope + active grant). Empty array for advertisers.
  // The fetch fires alongside memberships so the /admin picker has data ready
  // by the time the user lands.
  const refreshMyAdminStations = React.useCallback(async () => {
    if (!user?.id) {
      setMyAdminStations(null);
      return;
    }
    try {
      const data = await apiClient.get<AdminStation[]>('/stations/my-admin');
      setMyAdminStations(Array.isArray(data) ? data : []);
    } catch (e) {
      console.warn('[useAuth] my-admin fetch failed:', e);
      setMyAdminStations([]);
    }
  }, [user?.id]);

  React.useEffect(() => {
    if (!user?.id) {
      setMyAdminStations(null);
      return;
    }
    void refreshMyAdminStations();
  }, [user?.id, refreshMyAdminStations]);

  // CTV-591: hydrate hasPassword whenever the signed-in user changes. The
  // endpoint reads auth.users.encrypted_password via the admin API — cheap
  // enough to fire on every session change. Errors don't blow up the UI;
  // callers treat null as "don't nudge yet."
  // Same stale-closure trap as refreshMemberships (CTV-582): SignInModal
  // captures this callback at the pre-verify render when `session` is null.
  // If we gated on the captured `session?.user?.id`, the post-verify call
  // would early-return null before hitting the API, and the modal would
  // never see the fresh has-password value. Read the live Supabase session
  // at call time instead — it's populated the moment verifyOtp returns.
  // Deps `[]` so the reference is stable and no captured state goes stale.
  const refreshHasPassword = React.useCallback(async (): Promise<boolean | null> => {
    if (!selfServiceSupabase) {
      setHasPassword(null);
      return null;
    }
    const { data } = await selfServiceSupabase.auth.getSession();
    const currentUserId = data.session?.user?.id;
    if (!currentUserId) {
      setHasPassword(null);
      return null;
    }
    try {
      const resp = await apiClient.get<{ hasPassword: boolean }>('/auth/me/has-password');
      const value = Boolean(resp.hasPassword);
      setHasPassword(value);
      return value;
    } catch (e) {
      console.warn('[useAuth] has-password fetch failed:', e);
      return null;
    }
  }, []);

  React.useEffect(() => {
    if (!session?.user?.id) {
      setHasPassword(null);
      return;
    }
    void refreshHasPassword();
  }, [session?.user?.id, refreshHasPassword]);

  // Fetch the Phase 2b publisher-admin grant. The RPC ships in this branch
  // (migration 20260511160000), so any error here is a real failure — RPC
  // missing on this project, network outage, perms misconfigured. Deny by
  // default in those cases; the access-denied page is friendlier than
  // accidentally letting in a revoked-grant staff user.
  React.useEffect(() => {
    if (!session?.user?.id || !selfServiceSupabase) {
      setHasPublisherAdminGrant(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await selfServiceSupabase.rpc('has_publisher_admin_grant');
        if (cancelled) return;
        if (error) {
          console.warn('[useAuth] has_publisher_admin_grant rpc errored — denying:', error);
          setHasPublisherAdminGrant(false);
          return;
        }
        setHasPublisherAdminGrant(Boolean(data));
      } catch (e) {
        if (cancelled) return;
        console.warn('[useAuth] has_publisher_admin_grant threw — denying:', e);
        setHasPublisherAdminGrant(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session?.user?.id]);

  const value: AuthContextValue = React.useMemo(
    () => ({
      user,
      isAuthenticated: !!user,
      advertiserMemberships,
      refreshMemberships,
      seedMembership,
      selectedAdvertiser,
      setSelectedAdvertiserId,
      hasPublisherAdminGrant,
      refreshProfile,
      myAdminStations,
      refreshMyAdminStations,
      refreshHasPassword,

      signOut: async () => {
        if (selfServiceSupabase) {
          await selfServiceSupabase.auth.signOut();
        }
        clearWizardCaches();
        setSession(null);
        setProfile(null);
        setAdvertiserMemberships(null);
        setSelectedAdvertiserIdState(null);
        setHasPublisherAdminGrant(null);
        setMyAdminStations(null);
        setHasPassword(null);
      },

      updateProfile: async (patch) => {
        if (!user || !session?.user?.id) {
          // Anonymous callers (e.g. wizard step 1 before sign-in) silently
          // no-op — react-hook-form already holds the in-flight values until
          // the user authenticates. Once signed in, future updateProfile
          // calls persist to /profiles/me.
          return;
        }
        // Optimistic local merge — the wizard surfaces these values
        // immediately. The PUT below reconciles with the canonical row.
        const next: AdvertiserProfile = {
          userId: user.id,
          contactEmail: profile?.contactEmail ?? user.email,
          contactName: patch.contactName ?? profile?.contactName ?? user.contactName ?? null,
          contactPhone: patch.contactPhone ?? profile?.contactPhone ?? user.contactPhone ?? null,
        };
        setProfile(next);
        try {
          const saved = await apiClient.put<AdvertiserProfile>('/profiles/me', {
            contactName: next.contactName,
            contactPhone: next.contactPhone,
          });
          setProfile(saved);
        } catch (e) {
          console.warn('[useAuth] updateProfile failed:', e);
        }
      },

      requestOtp: async (email, stationSlug) => {
        if (!selfServiceSupabase) {
          throw new Error(
            'VITE_SELFSERVICE_SUPABASE_URL / VITE_SELFSERVICE_SUPABASE_ANON_KEY are not configured.',
          );
        }
        analytics.track(EVENTS.OTP_REQUESTED, { station_slug: stationSlug });

        // In dev (local AND the dev Render deploy), hit the NestJS dev-only
        // endpoint that uses the Supabase admin API to generate a magiclink
        // AND surfaces the 6-digit OTP back to the client. Saves the email
        // round-trip when the prod email links would be misrouted / email
        // isn't wired up yet. The endpoint also sends the email so users can
        // fall back to it if needed. isSelfServiceDevelopment() is true on
        // localhost and selfservice-frontend-dev.onrender.com, false in prod.
        if (isSelfServiceDevelopment()) {
          try {
            const { otp } = await apiClient.post<{ otp: string }>(
              '/auth/dev/issue-otp',
              { email, stationSlug },
              { anonymous: true },
            );
            return { devCode: otp };
          } catch (e) {
            if (!(e instanceof ApiError) || e.status !== 403) {
              console.warn('[useAuth] dev OTP endpoint failed; falling back to email:', e);
            }
          }
        }

        // Our backend mints the Supabase token via admin.generateLink and
        // dispatches the SendGrid `auth.signin` template. The stationSlug
        // (when present) drives per-station branding on the email.
        await apiClient.post(
          '/auth/sign-in/request',
          { email, stationSlug, redirectTo: `${window.location.origin}` },
          { anonymous: true },
        );
        return { devCode: '' };
      },

      verifyOtp: async (email, code) => {
        if (!selfServiceSupabase) {
          throw new Error('Supabase Auth not configured.');
        }
        const { data, error } = await selfServiceSupabase.auth.verifyOtp({
          email,
          token: code,
          type: 'email',
        });
        if (error) {
          analytics.track(EVENTS.OTP_FAILED, { reason: 'invalid_code' });
          throw new Error(error.message);
        }
        if (!data.session?.user) {
          analytics.track(EVENTS.OTP_FAILED, { reason: 'no_session' });
          throw new Error('OTP verified but no session returned.');
        }
        analytics.track(EVENTS.OTP_VERIFIED);
        // onAuthStateChange will pick up the new session asynchronously, but
        // we also synthesize the SelfServiceUser eagerly so the caller can
        // show "signed in as …" without waiting for a render. Profile fields
        // populate on the next render once /profiles/me resolves.
        const eager: SelfServiceUser = {
          id: data.session.user.id,
          email: data.session.user.email ?? email,
          role: mapRole(data.session.user),
          rawRole: extractRawRole(data.session.user),
          organizationId: extractOrganizationId(data.session.user),
          publisherSlug: extractStations(data.session.user)[0],
          hasPassword: null,
        };
        return eager;
      },

      canAdminStation: (config) => {
        if (!user || !user.rawRole) return false;
        if (user.rawRole === 'super_admin') return true;
        if (user.rawRole === 'organization_admin') {
          return !!config.organizationId && user.organizationId === config.organizationId;
        }
        if (user.rawRole === 'manager' || user.rawRole === 'sales_rep') {
          const claims = extractStations(session?.user);
          // config.name is the raw stations.name (= what JWT entries carry).
          return claims.includes(config.name);
        }
        return false;
      },
    }),
    [
      user,
      session,
      profile,
      advertiserMemberships,
      refreshMemberships,
      seedMembership,
      selectedAdvertiser,
      setSelectedAdvertiserId,
      hasPublisherAdminGrant,
      refreshProfile,
      myAdminStations,
      refreshMyAdminStations,
      refreshHasPassword,
    ],
  );

  // Don't render children until we've at least tried to read the existing
  // session — otherwise initial paint flashes "signed out" before the
  // persisted session loads.
  if (!bootstrapped) return null;

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth(): AuthContextValue {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
